"use client";

import { useEffect, useMemo, useRef, type JSX } from "react";
import {
  AtomicLayoutSection,
  AtomicSearchBox,
  AtomicSearchBoxQuerySuggestions,
  AtomicSearchBoxRecentQueries,
} from "@coveo/atomic-react";
import { loadPaginationActions, loadQueryActions } from "@coveo/headless";
import { ClientOnly } from "../../../atoms/ClientOnly";
import { useSearchEngine } from "../../../hooks/useSearchEngine";
import { type SearchBoxParams, type SearchBoxProps } from "./search-box.props";

interface SearchBoxConfig {
  minimumQueryLength: number;
  numberOfQueries: number;
  redirectionUrl?: string;
  suggestionTimeout: number;
  numberOfResults: number;
  maxSuggestionsWithQuery: number;
  maxSuggestionsWithoutQuery: number;
}

const toNumber = (value: string | undefined, fallback: number): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

// Reads all numeric/url params with sensible defaults so each variant reads
// the same shape without repeating `Number(...)` boilerplate.
const readSearchBoxConfig = (params: SearchBoxParams): SearchBoxConfig => ({
  minimumQueryLength: toNumber(params.MinimumQueryLength, 0),
  numberOfQueries: toNumber(params.NumberOfQueries, 8),
  redirectionUrl: params.RedirectionUrl ? params.RedirectionUrl : undefined,
  suggestionTimeout: toNumber(params.SuggestionTimeout, 400),
  numberOfResults: toNumber(params.ResultsPerPage, 17),
  maxSuggestionsWithQuery: toNumber(params.MaxWithQuery, 0),
  maxSuggestionsWithoutQuery: toNumber(params.MaxWithoutQuery, 0),
});

// Registers the pagination size on the Coveo engine. Done in an effect to
// avoid dispatching during render.
const usePaginationSize = (
  searchEngine: ReturnType<typeof useSearchEngine>,
  numberOfResults: number,
) => {
  useEffect(() => {
    const paginationCreator = loadPaginationActions(searchEngine);
    searchEngine.dispatch(
      paginationCreator.registerNumberOfResults(numberOfResults),
    );
  }, [searchEngine, numberOfResults]);
};

/* Removes a specific query parameter (e.g., `q`) from a URL string (? or #)
without rebuilding the entire query string, while preserving other params.*/
function removeParamFromRaw(raw: string, param: string): string {
  if (!raw) return raw;

  const prefix = raw[0];
  if (prefix !== "?" && prefix !== "#") return raw;

  const cleaned = raw
    .slice(1)
    .split("&")
    .filter(
      (item) =>
        item && !item.toLowerCase().startsWith(`${param.toLowerCase()}=`),
    )
    .join("&");

  return cleaned ? `${prefix}${cleaned}` : "";
}

/* Cleans a query or hash string by removing empty parameters (e.g., `sortCriteria=`)
 and fixing malformed separators (like extra `&`), while preserving valid params.*/
function normalizeUrlParams(raw: string): string {
  if (!raw) return "";

  const prefix = raw[0];
  if (prefix !== "?" && prefix !== "#") return raw;

  const cleaned = raw
    .slice(1)
    .split("&")
    .filter(
      (param) => param && !param.toLowerCase().startsWith("sortCriteria="),
    )
    .join("&");

  return cleaned ? `${prefix}${cleaned}` : "";
}

// Remove `q` and empty params from URL and notify Atomic to re-sync state
function removeQFromUrl(): void {
  if (typeof globalThis === "undefined") return;

  const { pathname, search, hash, href } = globalThis.location;

  const nextSearch = normalizeUrlParams(removeParamFromRaw(search, "q"));
  const nextHash = normalizeUrlParams(removeParamFromRaw(hash, "q"));

  const nextUrl = pathname + nextSearch + nextHash;
  const currentUrl = pathname + search + hash;

  if (nextUrl === currentUrl) return;

  const oldHref = href;
  globalThis.history.replaceState({}, globalThis.document.title, nextUrl);
  const newHref = globalThis.location.href;

  // Notify listeners of URL change so UrlManager re-syncs
  if (search !== nextSearch) {
    // replaceState doesn’t fire popstate, so we dispatch it to re-sync Atomic
    try {
      globalThis.dispatchEvent(
        new PopStateEvent("popstate", { state: globalThis.history.state }),
      );
    } catch {
      globalThis.dispatchEvent(new Event("popstate"));
    }
  }

  if (hash !== nextHash) {
    try {
      globalThis.dispatchEvent(
        new HashChangeEvent("hashchange", { oldURL: oldHref, newURL: newHref }),
      );
    } catch {
      globalThis.dispatchEvent(new Event("hashchange"));
    }
  }
}

export const Default = (props: SearchBoxProps): JSX.Element => {
  console.log("---->[SearchBox:Default] Component", props.fields);

  const { params } = props;
  const id = params?.RenderingIdentifier;
  const searchEngine = useSearchEngine(params?.SearchScope);
  const config = useMemo(() => readSearchBoxConfig(params), [params]);
  const disableSearchWithinResults =
    props?.params["DisableSearchWithinResults"] === "1";

  usePaginationSize(searchEngine, config.numberOfResults);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const searchBoxRef = useRef<HTMLAtomicSearchBoxElement | null>(null);

  useEffect(() => {
    const { updateQuery } = loadQueryActions(searchEngine);

    // Reset query state and clean `q` from the URL
    const clearQueryAndUrl = () => {
      searchEngine.dispatch(updateQuery({ q: "" }));

      removeQFromUrl();
      setTimeout(removeQFromUrl, 0);
      requestAnimationFrame(removeQFromUrl);
    };

    // Check if the clicked element is a search clear button
    const isClearElement = (node: unknown) => {
      if (!(node instanceof HTMLElement)) return false;

      const part = (node.getAttribute("part") ?? "").toLowerCase();
      const aria = (node.getAttribute("aria-label") ?? "").toLowerCase();
      const title = (node.getAttribute("title") ?? "").toLowerCase();
      const cls = (node.getAttribute("class") ?? "").toLowerCase();

      return (
        part.includes("clear") ||
        aria.includes("clear") ||
        title.includes("clear") ||
        cls.includes("clear")
      );
    };

    // Handle clicks inside the component and detect clear button clicks
    const onDocumentClickCapture = (evt: MouseEvent) => {
      const path = (evt.composedPath?.() || []) as unknown[];

      const insideThisComponent = path.some(
        (n) => n instanceof Node && !!containerRef.current?.contains(n),
      );
      if (!insideThisComponent) return;

      if (path.some(isClearElement)) {
        queueMicrotask(clearQueryAndUrl);
      }
    };

    document.addEventListener("click", onDocumentClickCapture, true);

    const inputCleanup: Array<() => void> = [];
    let observer: MutationObserver | null = null;

    // Attach input listeners to inner search field if available
    const bindToInnerInputIfPossible = () => {
      const host = searchBoxRef.current;
      const root: ShadowRoot | null = host?.shadowRoot ?? null;
      if (!root) return false;

      const input =
        root.querySelector<HTMLInputElement>('input[type="search"]') ||
        root.querySelector<HTMLInputElement>("input");

      if (!input) return false;

      const onInputOrSearch = () => {
        if (input.value === "") {
          clearQueryAndUrl();
        }
      };

      input.addEventListener("input", onInputOrSearch);
      input.addEventListener("search", onInputOrSearch);

      inputCleanup.push(
        () => input.removeEventListener("input", onInputOrSearch),
        () => input.removeEventListener("search", onInputOrSearch),
      );

      return true;
    };

    const boundNow = bindToInnerInputIfPossible();

    if (!boundNow) {
      observer = new MutationObserver(() => {
        if (bindToInnerInputIfPossible() && observer) {
          observer.disconnect();
          observer = null;
        }
      });

      const tryObserve = () => {
        const host = searchBoxRef.current;
        const root: ShadowRoot | null = host?.shadowRoot ?? null;

        if (root && observer) {
          observer.observe(root, { childList: true, subtree: true });
        } else {
          requestAnimationFrame(tryObserve);
        }
      };

      tryObserve();
    }

    return () => {
      document.removeEventListener("click", onDocumentClickCapture, true);
      inputCleanup.forEach((fn) => fn());
      if (observer) observer.disconnect();
    };
  }, [searchEngine]);

  return (
    <div
      ref={containerRef}
      className={`search-box mb-5 mt-(--spacing-thirtytwo) px-(--spacing-sixteen) ${params?.styles ?? ""}`}
      data-testid="search-box"
      data-role="search-box-component"
    >
      <ClientOnly>
        <AtomicLayoutSection id={id ?? undefined} section="search">
          <AtomicSearchBox
            ref={searchBoxRef}
            minimumQueryLength={config.minimumQueryLength}
            numberOfQueries={config.numberOfQueries}
            redirectionUrl={config.redirectionUrl}
            suggestionTimeout={config.suggestionTimeout}
            clearFilters={disableSearchWithinResults}
          >
            <AtomicSearchBoxQuerySuggestions
              maxWithQuery={config.maxSuggestionsWithQuery}
              maxWithoutQuery={config.maxSuggestionsWithoutQuery}
            />
            <AtomicSearchBoxRecentQueries
              maxWithQuery={0}
              maxWithoutQuery={0}
            />
          </AtomicSearchBox>
        </AtomicLayoutSection>
      </ClientOnly>
    </div>
  );
};

// Inline styles for the HeaderIcon variant — kept verbatim from the original
// because they target Coveo Atomic's shadow DOM via ::part() selectors and
// must stay co-located with the variant.
const HEADER_ICON_STYLES = `
  @keyframes slideInFromRight {
    from { transform: translateX(100%); opacity: 0; }
    to { transform: translateX(0); opacity: 1; }
  }
  .search-box-container atomic-search-box::part(wrapper) {
    border: none;
    border-radius: 8px;
    background-color: white;
    bottom: 20px;
  }
  .search-box-container atomic-search-box::part(wrapper):focus-within {
    box-shadow: none !important;
    border: none;
  }
  .search-box-container atomic-search-box::part(input) {
    font-size: 24px;
    border: none !important;
    outline: none !important;
    color: #67768b;
    letter-spacing: 0.8px;
    height: 78px;
  }
  .search-box-container atomic-search-box::part(input)::placeholder {
    color: #337ab7;
    opacity: 1;
  }
  .search-box-container atomic-search-box::part(submit-button-wrapper) {
    padding-right: 20px;
    padding-top: 18px;
    padding-bottom: 0;
  }
  .search-box-container atomic-search-box::part(submit-button) {
    background-color: transparent;
    margin-right: 8px;
    width: 78px;
    height: 78px;
    margin-top: 2px;
  }
  .search-box-container atomic-search-box::part(ripple) {
    background: transparent !important;
    opacity: 0 !important;
  }
  .search-box-container atomic-search-box::part(submit-icon) {
    color: #1d4f76;
    width: 28px;
    height: 28px;
  }
  .search-box-container atomic-search-box::part(submit-button):focus {
    outline: 2px solid #00050b;
    outline-offset: 2px;
    border-radius: 1px;
  }
  .search-box-container atomic-search-box::part(clear-button-wrapper) {
    padding-right: 20px;
    padding-top: 18px;
    padding-bottom: 0;
  }
  .search-box-container atomic-search-box::part(loading) {
    margin-top: 15px;
    margin-right: 20px;
  }
  .search-box-container atomic-search-box::part(clear-button) {
    animation: slideInFromRight 0.3s ease;
    margin-top: 8px;
  }
  .search-box-container atomic-search-box::part(clear-icon) {
    color: #000;
    width: 20px;
    height: 20px;
    opacity: 0.6;
  }
  .search-box-container atomic-search-box::part(submit-icon):hover {
    color: #006bd3;
  }
  @media (max-width: 1024px) {
    .search-box-container atomic-search-box::part(input) {
      font-size: 16px;
      letter-spacing: 0.4px;
      line-height: 22.8571px;
      height: 64px;
    }
    .search-box-container atomic-search-box::part(submit-button) {
      width: 64px;
      height: 64px;
    }
  }
`;

// Polls Coveo's web component for its shadow-root inputs/buttons and applies
// accessible labels. Polling because <atomic-search-box> upgrades async; the
// first render after upgrade still doesn't expose shadowRoot synchronously.
const useAtomicSearchBoxLabels = (
  searchBoxRef: React.RefObject<HTMLDivElement | null>,
  placeholderText: string | undefined,
  searchButtonText: string,
) => {
  useEffect(() => {
    if (!placeholderText) return;
    const interval = setInterval(() => {
      const atomicSearchBox =
        searchBoxRef.current?.querySelector("atomic-search-box");
      if (!atomicSearchBox?.shadowRoot) return;

      const input =
        atomicSearchBox.shadowRoot.querySelector('[part="textarea"]');
      const submitButton = atomicSearchBox.shadowRoot.querySelector(
        '[part="submit-button-wrapper"] > button',
      );

      if (input) {
        input.setAttribute("placeholder", placeholderText);
        input.setAttribute("title", "Insert a query .press enter to send");
        input.setAttribute("aria-label", "Insert a query .press enter to send");
        clearInterval(interval);
      }
      if (submitButton) {
        submitButton.setAttribute("title", searchButtonText);
        submitButton.setAttribute("aria-label", searchButtonText);
      }
    }, 100);

    return () => clearInterval(interval);
  }, [searchBoxRef, placeholderText, searchButtonText]);
};

// Coveo re-creates the clear-button on every keystroke, so a one-shot
// labelling pass isn't enough — observe the shadow root and re-apply.
const useAtomicSearchBoxClearButtonLabel = (
  searchBoxRef: React.RefObject<HTMLDivElement | null>,
) => {
  useEffect(() => {
    const searchBoxEl =
      searchBoxRef.current?.querySelector("atomic-search-box");
    if (!searchBoxEl) return;

    const applyClearButtonFix = () => {
      const clearButton = searchBoxEl.shadowRoot?.querySelector(
        '[part="clear-button-wrapper"] > button',
      );
      if (clearButton) {
        clearButton.setAttribute("title", "Clear");
        clearButton.setAttribute("aria-label", "Clear");
      }
    };

    applyClearButtonFix();

    const observer = new MutationObserver(applyClearButtonFix);
    if (searchBoxEl.shadowRoot) {
      observer.observe(searchBoxEl.shadowRoot, {
        childList: true,
        subtree: true,
      });
    }
    return () => observer.disconnect();
  }, [searchBoxRef]);
};

export const HeaderIcon = (props: SearchBoxProps): JSX.Element => {
  console.log("---->[SearchBox:HeaderIcon] Component", props.fields);

  const { fields, params } = props;
  const id = params?.RenderingIdentifier;
  const searchBoxRef = useRef<HTMLDivElement>(null);
  const searchEngine = useSearchEngine(params?.SearchScope);
  const config = useMemo(() => readSearchBoxConfig(params), [params]);

  const placeholderText = fields?.TextBoxText?.value;
  const searchButtonText = fields?.SearchButtonText?.value
    ? fields?.SearchButtonText?.value
    : "";
  const disableSearchWithinResults =
    props?.params["DisableSearchWithinResults"] === "1";

  useAtomicSearchBoxLabels(searchBoxRef, placeholderText, searchButtonText);
  useAtomicSearchBoxClearButtonLabel(searchBoxRef);
  usePaginationSize(searchEngine, config.numberOfResults);

  return (
    <>
      <style>{HEADER_ICON_STYLES}</style>

      <ClientOnly>
        <AtomicLayoutSection id={id ?? undefined} section="search">
          <div
            className={`search-box-container ${params?.styles ?? ""} absolute top-23.75 lg:top-[calc(100%+16px)] mx-4 right-0 bg-white h-19 xl:h-19.5 z-50 w-[calc(100%-32px)] lg:w-[calc(50%-30px)] shadow-[0_0_8px_5px_rgba(0,80,117,0.3)] rounded-md transition-all duration-300 ease-in-out before:content-[''] before:absolute before:-top-3.5 before:right-2.75 before:w-3.5 before:h-3.5 before:border-r-14 before:border-l-14 before:border-b-14 before:border-transparent before:border-b-white`}
          >
            <div
              className="search-box-container__search-bar relative w-full top-2.5 pt-2.5"
              ref={searchBoxRef}
            >
              <AtomicSearchBox
                minimumQueryLength={config.minimumQueryLength}
                numberOfQueries={config.numberOfQueries}
                redirectionUrl={config.redirectionUrl}
                suggestionTimeout={config.suggestionTimeout}
                clearFilters={disableSearchWithinResults}
              >
                <AtomicSearchBoxQuerySuggestions
                  maxWithQuery={0}
                  maxWithoutQuery={0}
                />
                <AtomicSearchBoxRecentQueries
                  maxWithQuery={0}
                  maxWithoutQuery={0}
                />
              </AtomicSearchBox>
            </div>
          </div>
        </AtomicLayoutSection>
      </ClientOnly>
    </>
  );
};
